/** Stroke icons from the section 11.0 mockup. Decorative: pair them with text or a label. */

const common = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
} as const;

export function ClockIcon() {
  return (
    <svg width="17" height="17" strokeWidth="2.4" {...common}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

/** A plus: "Add to open ticket T…?" (v0.9.0's P4). */
export function PlusIcon() {
  return (
    <svg width="13" height="13" strokeWidth="2.4" {...common}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

/** A tray: the open-tickets drawer's tab, on a phone (ADR 0190). */
export function TrayIcon() {
  return (
    <svg width="16" height="16" strokeWidth="1.9" {...common}>
      <path d="M22 12h-6l-2 3h-4l-2-3H2" />
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </svg>
  );
}

export function ChevronLeftIcon() {
  return (
    <svg width="16" height="16" strokeWidth="2" {...common}>
      <path d="m15 18-6-6 6-6" />
    </svg>
  );
}

export function ChevronRightIcon() {
  return (
    <svg width="16" height="16" strokeWidth="2" {...common}>
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

/** An eye with a line through it: privacy mode (ADR 0144), as Actual Budget's. */
export function EyeOffIcon() {
  return (
    <svg width="18" height="18" strokeWidth="2" {...common}>
      <path d="M10.7 5.1A10.4 10.4 0 0 1 12 5c6.5 0 10 7 10 7a17.6 17.6 0 0 1-2.8 3.8" />
      <path d="M6.6 6.6C3.6 8.6 2 12 2 12s3.5 7 10 7a9.7 9.7 0 0 0 5.4-1.6" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
      <path d="m2 2 20 20" />
    </svg>
  );
}

// The source icons (section 11.0 mockup's cards, rows and lanes), network's
// since ADR 0165.

export function PhoneIcon() {
  return (
    <svg width="16" height="16" strokeWidth="1.8" {...common}>
      <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2z" />
    </svg>
  );
}

/**
 * ScreenConnect: its logo's two frames, a screen with another behind it, up
 * and to the right, drawn in this set's stroke style (the owner, 2026-10-19:
 * "make a simple version"). Traced from the logo's proportions.
 */
export function RemoteIcon() {
  return (
    <svg width="16" height="16" strokeWidth="1.8" {...common}>
      <rect x="3.1" y="9" width="13.9" height="10" rx="0.6" />
      <path d="M7.4 5H20.9V16.3" />
    </svg>
  );
}

/**
 * Datto RMM: its logo's pulse line in a circle, the line traced point for
 * point from the logo (the owner, 2026-10-19: "the line in the circle needs
 * to be the exact same way"), in this set's stroke style.
 */
export function RmmIcon() {
  return (
    <svg width="16" height="16" strokeWidth="1.8" {...common}>
      <circle cx="12" cy="12" r="9" />
      <path d="M3.26 14.14L4.98 14.14L7.17 9.56L9.2 15.71L12.61 6.51L15.61 13.68L17.44 10.12L19.32 13.42L20.87 13.53" />
    </svg>
  );
}

export function EmailIcon() {
  return (
    <svg width="16" height="16" strokeWidth="1.8" {...common}>
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 6-10 7L2 6" />
    </svg>
  );
}

/** Three linked boxes: network access (ADR 0165), and its Other service. */
export function NetworkIcon() {
  return (
    <svg width="16" height="16" strokeWidth="1.8" {...common}>
      <rect x="16" y="16" width="6" height="6" rx="1" />
      <rect x="2" y="16" width="6" height="6" rx="1" />
      <rect x="9" y="2" width="6" height="6" rx="1" />
      <path d="M5 16v-3a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v3M12 12V8" />
    </svg>
  );
}

// Each network service's (ADR 0165): beside its name in a block's details.

/** A prompt: SSH. */
export function TerminalIcon() {
  return (
    <svg width="16" height="16" strokeWidth="1.8" {...common}>
      <path d="m4 17 6-6-6-6M12 19h8" />
    </svg>
  );
}

/**
 * A screen with Remote Desktop's two arrows on a badge, one above and left,
 * one below and right, pointing in at each other, clear of the ring: RDP. The look the owner
 * asked for, the remote-desktop app's (2026-10-19), drawn in this set's
 * stroke style. Not ScreenConnect's bare screen.
 */
export function RdpIcon() {
  return (
    <svg width="16" height="16" strokeWidth="1.8" {...common}>
      <path d="M11.5 14H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v4.5" />
      <path d="M7 20h5M9.5 14v6" />
      <circle cx="18" cy="16.5" r="5" />
      <path d="m16.6 14.2 1.1 1.1-1.1 1.1M19.4 16.6l-1.1 1.1 1.1 1.1" />
    </svg>
  );
}

/**
 * A brick wall with a flame: firewall management, the familiar firewall
 * picture the owner asked for (2026-10-19), in this set's stroke style.
 */
export function FirewallIcon() {
  return (
    <svg width="16" height="16" strokeWidth="1.8" {...common}>
      <rect x="9" y="8" width="13" height="11" rx="1.5" />
      <path d="M9 11.67h13M9 15.33h13M15.5 8v3.67M12.25 11.67v3.66M18.75 11.67v3.66M15.5 15.33V19" />
      <path d="M5 21a3 3 0 0 0 3-3c0-2-1.4-2.9-1.7-4.8-.8 1.1-1.1 2-1.1 3.1-.7-.7-1-1.5-1-2.7C2.9 14.6 2 16 2 18a3 3 0 0 0 3 3z" />
    </svg>
  );
}

/** A tower server: server management (iDRAC/iLO). Not RMM's rack. */
export function ServerIcon() {
  return (
    <svg width="16" height="16" strokeWidth="1.8" {...common}>
      <rect x="5" y="2" width="14" height="20" rx="2" />
      <path d="M9 6h6M9 10h6M12 17h.01" />
    </svg>
  );
}

/** A globe: web (HTTPS). */
export function GlobeIcon() {
  return (
    <svg width="16" height="16" strokeWidth="1.8" {...common}>
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  );
}

/** Copy: two sheets (the block menus' Copy summary and Copy ticket number; ADR 0194). */
export function CopyIcon() {
  return (
    <svg width="16" height="16" strokeWidth="1.9" {...common}>
      <rect x="9" y="9" width="12" height="12" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

/** Opens elsewhere: a box with an arrow out of it (Open ticket in Autotask; ADR 0194). */
export function ExternalIcon() {
  return (
    <svg width="16" height="16" strokeWidth="1.9" {...common}>
      <path d="M15 3h6v6" />
      <path d="M10 14 21 3" />
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    </svg>
  );
}

/** Opens out: two arrows apart (a card menu's Open; ADR 0194). */
export function ExpandIcon() {
  return (
    <svg width="14" height="14" strokeWidth="2.2" {...common}>
      <path d="M15 3h6v6" />
      <path d="M9 21H3v-6" />
      <path d="M21 3l-7 7" />
      <path d="M3 21l7-7" />
    </svg>
  );
}

/** A cross: Clear the selection (ADR 0194, decision 5). */
export function CrossIcon() {
  return (
    <svg width="14" height="14" strokeWidth="2" {...common}>
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

/** A tick: a selected card's badge (ADR 0194, decision 5). */
export function CheckIcon() {
  return (
    <svg width="12" height="12" strokeWidth="3" {...common}>
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
  );
}
